/**
 * 8.4 文档批注列表
 * 从当前文档的 blocks 中提取所有 annotations，
 * 在 Drawer 中展示，点击跳转到对应 block。
 */

import { useMemo } from 'react';
import { MessageSquare } from 'lucide-react';
import { Drawer, IconButton } from '@hump/ui';
import { useReaderStore } from '../../stores/reader';
import type { Annotation } from '@hump/shared-types';

interface AnnotationWithBlock extends Annotation {
  blockId: string;
  blockOriginal: string;
}

/** 截断原文到指定长度 */
function truncate(text: string, max = 60): string {
  const clean = text.replace(/\s+/g, ' ').trim();
  return clean.length > max ? clean.slice(0, max) + '…' : clean;
}

export function AnnotationList() {
  const blocks = useReaderStore((s) => s.blocks);
  const annotationListOpen = useReaderStore((s) => s.annotationListOpen);
  const setAnnotationListOpen = useReaderStore((s) => s.setAnnotationListOpen);

  // 从 blocks 中提取所有批注（附带来源 block 原文）
  const items = useMemo<AnnotationWithBlock[]>(() => {
    const result: AnnotationWithBlock[] = [];
    for (const b of blocks) {
      if (b.annotations && b.annotations.length > 0) {
        for (const a of b.annotations) {
          result.push({
            ...a,
            blockId: b.id,
            blockOriginal: b.original,
          });
        }
      }
    }
    // 按创建时间倒序（最新在前）
    return result.sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [blocks]);

  function scrollToBlock(blockId: string) {
    const el = document.querySelector(`[data-block-id="${blockId}"]`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // 短暂高亮
      el.classList.add('ring-2', 'ring-camel', 'ring-offset-2');
      setTimeout(() => {
        el.classList.remove('ring-2', 'ring-camel', 'ring-offset-2');
      }, 1500);
    }
    setAnnotationListOpen(false);
  }

  return (
    <>
      <IconButton
        size="sm"
        onClick={() => setAnnotationListOpen(true)}
        title="批注列表"
        aria-label="批注列表"
      >
        <MessageSquare className="w-4 h-4" />
        {items.length > 0 && (
          <span className="ml-0.5 text-xs text-camel">{items.length}</span>
        )}
      </IconButton>

      <Drawer
        open={annotationListOpen}
        onClose={() => setAnnotationListOpen(false)}
        title={`批注（${items.length}）`}
      >
        {items.length === 0 ? (
          <p className="text-sm text-charcoal/50 dark:text-charcoal-light/50 text-center py-8">
            当前文档暂无批注
          </p>
        ) : (
          <ul className="space-y-3">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollToBlock(item.blockId)}
                  className="w-full text-left rounded-md border border-border-warm dark:border-border-warm-dark px-3 py-2 hover:bg-warm-white dark:hover:bg-border-warm-dark/30 transition-colors"
                >
                  {/* 批注内容 */}
                  <p className="text-sm text-charcoal dark:text-charcoal-light whitespace-pre-wrap break-words">
                    {item.text}
                  </p>
                  {/* 来源 block 原文（截断） */}
                  <p className="mt-1.5 text-xs text-charcoal/50 dark:text-charcoal-light/50 italic border-l-2 border-border-warm dark:border-border-warm-dark pl-2">
                    {truncate(item.blockOriginal)}
                  </p>
                  {/* 时间 */}
                  <p className="mt-1 text-xs text-charcoal/40 dark:text-charcoal-light/40">
                    {new Date(item.createdAt).toLocaleString('zh-CN')}
                  </p>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Drawer>
    </>
  );
}
